Accounts = require 'common/lib/accounts'
ApiAjax = require 'network/api-ajax'
ApiError = require 'network/api-error'
ApiPromise = require 'network/api-promise'
Auth = require 'db/auth'
Controller = require 'controller'
Member = require 'models/member'
LimitMixin = require 'lib/limit-mixin'
MembershipModel = require 'models/internal/membership-model'
ModelWithPreferences = require 'models/internal/model-with-preferences'
OrgFeatures = require 'data/org-features'
AttachmentTypes = require 'data/attachment-types'
Promise = require 'bluebird'
SlackAjax = require 'network/slack-ajax'
TagList = require 'models/collections/tag-list'
PluginDataList = require 'models/collections/plugin-data-list'
isTrello = require 'lib/util/url/is-trello'
_ = require 'underscore'
mixin = require 'lib/mixin'
track = require 'debug/track'

module.exports = class Organization extends ModelWithPreferences
  mixin MembershipModel, LimitMixin, @

  typeName: 'Organization'
  nameAttr: 'displayName'
  urlRoot: '/1/organizations'

  initialize: ->
    super()

    @_slackAssociation = null

  @lazy
    invitationList: ->
      # Dependency required at call site to avoid import cycles, do not lift to top of module
      OrganizationInvitationList = require 'models/collections/organization-invitation-list'
      new OrganizationInvitationList([])
    boardList: ->
      # Dependency required at call site to avoid import cycles, do not lift to top of module
      BoardList = require 'models/collections/board-list'
      new BoardList().syncModel(@, 'idBoards')
    memberList: ->
      # Dependency required at call site to avoid import cycles, do not lift to top of module
      MemberList = require 'models/collections/member-list'
      new MemberList().syncModel(@, 'memberships', { fxGetIds: (memberships) -> _.pluck(memberships, 'idMember') })
    adminList: ->
      # Dependency required at call site to avoid import cycles, do not lift to top of module
      MemberList = require 'models/collections/member-list'
      new MemberList().syncModel(@, 'memberships', {
        fxGetIds: (memberships) =>
          # The only admins of organizations are those flagged as such on the
          # memberships subdoc.
          isActiveAdmin = ({ memberType, deactivated }) ->
            # NOTE: Can't check for deactivated == false; deactivated might not
            # be set, e.g. for non BC orgs
            memberType == 'admin' && !deactivated
          _.pluck(_.filter(memberships, isActiveAdmin), 'idMember')
      })
    collaboratorList: ->
      # Dependency required at call site to avoid import cycles, do not lift to top of module
      MemberList = require 'models/collections/member-list'
      new MemberList().syncModel(@, 'collaborators', {
        fxGetIds: (member) -> _.pluck(member, 'id')
      })
    tagList: ->
      new TagList()
      .syncSubModels @, 'tags'
    pluginDataList: ->
      new PluginDataList([], {
        scopeModel: @
      })
      .syncCache @modelCache, [], (pluginData) =>
        pluginData.get('idModel') == @id && pluginData.get('scope') == 'organization'

  prefNames: ['permissionLevel', 'orgInviteRestrict', 'boardVisibilityRestrict', 'boardDeleteRestrict', 'externalMembersDisabled']

  editable: ->
    @ownedByMember(Auth.me())

  isPublic: -> @getPref('permissionLevel') == 'public'

  isPremium: ->
    OrgFeatures.hasProduct(@get('products'))

  isBusinessClass: ->
    @isPremium() and not @belongsToRealEnterprise()

  # note: this is identical to isRealEnterprise in models/enterprise.coffee
  belongsToRealEnterprise: ->
    OrgFeatures.isFeatureEnabled('enterpriseUI', @get('products'))

  hasAccount: ->
    Accounts.hasAccount(@get('paidAccount')?.standing)

  isPremiumMonthly: ->
    @isPremium() && OrgFeatures.isMonthly(@get('products'))

  isPremiumYearly: ->
    @isPremium() && OrgFeatures.isYearly(@get('products'))

  calculateTotalPrice: (users) ->
    OrgFeatures.getPrice(@get('products')) * users

  isEnterprise: ->
    @get('idEnterprise')?

  paysWithPurchaseOrder: ->
    OrgFeatures.usesPurchaseOrder(@get('products')) ||
    @get('paidAccount')?.contactEmail == 'purchase-order@trello.com'

  getBillingStrategy: (product) ->
    if OrgFeatures.isPerUser product
      perUser: true
      perActiveUser: OrgFeatures.isPerActiveUser product
      billableCollaboratorCount: @get('billableCollaboratorCount')
      userCount:
        if OrgFeatures.isPerActiveUser product
          @get('activeBillableMemberCount')
        else
          @get('billableMemberCount')
    else
      flatRate: true

  totalPerUserPrice: (product) ->
    if !product
      return 0

    billingStrategy = @getBillingStrategy(product)
    paidAccount = @get('paidAccount')
    productCode = [OrgFeatures.getProductNumber(product)]
    billingDate = new Date(paidAccount?.billingDates?[productCode])
    gracePeriodEndDate = new Date('2018-12-17T08:00:00.000Z')
    shouldIncludeBillableCollaborators = (
      OrgFeatures.isFeatureEnabled('multiBoardGuests', productCode) ||
      (
        OrgFeatures.isFeatureEnabled('toBeUpgradedToMBGPlan', productCode) &&
        # billing date may not exist in the case of an organization purchasing a different product
        billingDate.getTime() &&
        billingDate.getTime() >= gracePeriodEndDate.getTime()
      )
    )

    if billingStrategy.perUser
      if shouldIncludeBillableCollaborators
        (billingStrategy.userCount + billingStrategy.billableCollaboratorCount) * OrgFeatures.getPrice(product)
      else
        billingStrategy.userCount * OrgFeatures.getPrice(product)

  needsBCUpgrade: ->
    _.intersection([OrgFeatures.getProductNumber(OrgFeatures.currentProducts.monthly), OrgFeatures.getProductNumber(OrgFeatures.currentProducts.yearly)], @get('products')).length == 0 && !@isEnterprise()

  isBC2PO: ->
    101 in @get('products')

  isPO: ->
    _.intersection([100, 101, 102, 107, 108, 109, 112], @get('products')).length

  isMember: (member) ->
    @getMemberType(member) in ['virtual', 'normal', 'admin']

  ownedByMember: (member) ->
    @getMemberType(member) == 'admin'

  canAddBoard: (board) ->
    vis = board.get('prefs').permissionLevel

    if vis == 'enterprise' && !@isEnterprise()
      return false
    if !@isFeatureEnabled('restrictVis')
      return true
    @canSetVisibility(vis)

  canDeleteBoard: (board) ->
    vis = board.get('prefs').permissionLevel
    pref = @get('prefs').boardDeleteRestrict[vis]

    return !pref || pref == 'org' || (pref == 'admin' && @owned())

  canSetVisibility: (vis) ->
    if vis == 'enterprise' && !@isEnterprise()
      return false

    if !@isFeatureEnabled('restrictVis')
      return true
    pref = @get('prefs').boardVisibilityRestrict[vis]

    !pref || pref == 'org' || pref == 'admin' && @owned()

  owned: -> @getMemberType(Auth.me()) == 'admin'

  isLinkedToGoogleApps: ->
    @isFeatureEnabled('googleApps') && @get('prefs').associatedDomain

  isFeatureEnabled: (feature) ->
    OrgFeatures.isFeatureEnabled(feature, @get('products'))

  snoopIsFeatureEnabled: (feature) ->
    @snoop('products')
    .map => @isFeatureEnabled(feature)
    .distinct()

  toJSON: (opts = {}) ->
    data = super

    if opts.url
      data.url = Controller.getOrganizationUrl(@get('name'))

    data.accountStanding = Accounts.standingName(@get('paidAccount')?.standing)

    data

  getPermLevel: -> @get('prefs').permissionLevel

  getAvailableRoles: ->
    if @isFeatureEnabled('superAdmins')
      ['superadmin', 'normal']
    else
      ['admin', 'normal']

  hasObservers: -> false

  setPluginData: (idPlugin, visibility, data) ->
    @pluginDataList.upsert(idPlugin, visibility, data)

  getPluginData: (idPlugin) ->
    @pluginDataList.dataForPlugin(idPlugin)

  snoopPluginData: (idPlugin) ->
    @pluginDataList.snoopDataForPlugin(idPlugin)

  getPluginCount: ->
    ApiPromise
      url: "/1/organizations/#{@id}/plugins"
      method: 'GET'
    .then (plugins) =>
      plugins.length

  getPublicBoardCount: ->
    ApiPromise
      url: "/1/organizations/#{@id}/boards"
      method: 'GET'
      data:
        filter: 'public'
        fields: 'id'
    .then (publicBoards) =>
      publicBoards.length

  invitationUrl: (secret) ->
    Controller.getOrganizationInvitionLinkUrl(secret)

  externalMembersDisabled: ->
    @isFeatureEnabled('disableExternalMembers') && @getPref('externalMembersDisabled')

  reactivateMember: (member) ->
    @setOnMembership(member, { deactivated: false })

    @addMembers(member, { reactivate: true })
    .then (grouped) =>
      if (!grouped[grouped._categories.ADDED])
        @setOnMembership(member, { deactivated: true })
      grouped

  deactivateMember: (member) ->
    ApiAjax
      url: "/1/organizations/#{ @id }/members/#{ member.id }/deactivated"
      type: 'PUT'
      data:
        value: true
      success: ->
        track("Organization", "Deactivate Member From Organization")

    @setOnMembership(member, { deactivated: true })
    return

  addMember: (member) ->
    # Enterprise admins will have a memberType of 'admin' even if they are
    # not real members of the team.
    type = if @getMemberType(member) == 'admin'
      'admin'
    else
      'normal'

    ApiPromise
      url: "/1/organizations/#{@id}/members/#{member.id}"
      type: 'put'
      data: { type }
      dataType: 'json'
    .then (data) =>
      if !data.token? && !@memberList.find((m) => m.id == member.id)
        @memberList.add(member)
        track("Invitations", "Add Member to Organization", "Role: Member")

  updateBulkAddMemberErrors: (errorCategories) ->
    existingErrors = @get('bulkAddMemberErrors')
    errorMessages = null
    changed = false

    # null clears errors
    if (errorCategories == null && existingErrors)
      changed = true
    else
      if existingErrors
        for category in _.values(existingErrors._categories) when errorCategories[category]?.length > 0
          existingErrors[category] = _.union(existingErrors[category] || [], errorCategories[category])
        errorMessages = existingErrors
        changed = true
      else if !_.isEmpty(_.omit(errorCategories, '_categories', '_categoryOrder'))
        errorMessages = errorCategories
        changed = true

    # don't want to trigger a react update if there's no change
    if changed
      @set 'bulkAddMemberErrors', errorMessages
      @trigger 'change:bulkAddMemberErrors'

  getBulkAddMemberErrors: ->
    @get('bulkAddMemberErrors')

  # checks to see if input is already a member
  getMemberFromUser = (user) ->
    if user?.id
      if !user.idOrganizations && user.attributes
        user.idOrganizations = user.attributes.idOrganizations || []
      Promise.resolve user
    else if _.isString(user)
      # need to fetch member
      ApiPromise
        method: 'get'
        url: "/1/members/#{ user }"
        data: { fields: 'idOrganizations' }
      .catch ApiError.NotFound, (err) ->
        null
    else
      Promise.resolve null

  addMembers: (users, options, progressHandler) ->
    if typeof options == 'function'
      progressHandler = options
      options = undefined

    CATEGORIES = {
      RESTRICTED: 'restricted'
      MUST_REACTIVATE: 'must-reactivate'
      USERNAME_NOT_FOUND: 'username-not-found'
      NOT_IN_ENTERPRISE: 'not-in-enterprise'
      NO_ENTERPRISE_LICENSES: 'no-enterprise-licenses'
      RATE_LIMIT: 'rate-limit-exceeded'
      UNKNOWN: 'unknown'
      EXISTING: 'existing'
      ADDED: 'added'
      TOO_MANY_MEMBERS: "too-many-members"
      MEMBER_TOO_MANY_ORGS: "member-too-many-orgs"
      DEACTIVATED_IN_THE_ENTERPRISE: "deactivated-in-the-enterprise"
      MUST_BE_MANAGED_ENT_MEMBER_OR_VALID_EMAIL: "not-managed-ent-member-or-valid-email"
      MUST_BE_MANAGED_ENT_MEMBER: "not-managed-ent-member"
    }

    # This is the order the errors are displayed; sorted by most to least interesting
    CATEGORY_ORDER = [
      CATEGORIES.RESTRICTED
      CATEGORIES.MUST_REACTIVATE
      CATEGORIES.USERNAME_NOT_FOUND
      CATEGORIES.NOT_IN_ENTERPRISE
      CATEGORIES.NO_ENTERPRISE_LICENSES
      CATEGORIES.UNKNOWN
      CATEGORIES.EXISTING
      CATEGORIES.TOO_MANY_MEMBERS
      CATEGORIES.MEMBER_TOO_MANY_ORGS
      CATEGORIES.DEACTIVATED_IN_THE_ENTERPRISE
      CATEGORIES.MUST_BE_MANAGED_ENT_MEMBER_OR_VALID_EMAIL
      CATEGORIES.MUST_BE_MANAGED_ENT_MEMBER
      CATEGORIES.ADDED
    ]

    emailRegex = /^[^@]+@[a-z0-9-]+(\.[a-z0-9-]+)+$/i
    users = if users.length? then users else [users]

    updateProgress = (completed) ->
      progressHandler?({ completed, length: users.length })

    completed = 0
    updateProgress(completed)
    Promise.map(users, (user) =>
      getMemberFromUser(user)
      .then (member) =>
        if member?
          if @id in member.idOrganizations
              throw new ApiError.Conflict("already in organization")

          if options?.reactivate
            ApiPromise
              method: 'put'
              url: "/1/organizations/#{@id}/members/#{member.id}/deactivated"
              data:
                value: false
          else
            # We can't call @getMemberType here because `member` here isn't
            # really a member doc.
            type = if @isEnterprise() && @getEnterprise()?.isAdmin(member)
              'admin'
            else
              'normal'

            ApiPromise
              method: 'put'
              url: "/1/organizations/#{@id}/members/#{member.id}"
              data: { type }
        else if emailRegex.test(user)
          ApiPromise
            method: 'post'
            url: "/1/organizations/#{@id}/memberships"
            data:
              email: user
              type: 'normal'
        else if emailRegex.test(user.email)
          ApiPromise
            method: 'put'
            url: "/1/organizations/#{@id}/members"
            data: user
            dataType: 'json'
          .then (data) =>
            member = new Member(data, { @modelCache })
            if !@memberList.get(member.id)
              @memberList.add(member)
            track("Invitations", "Send Email Org Invite to New Member", "Role: #{data.type}")
        else
          throw new ApiError.NotFound("username not found")
      .then (response) ->
        CATEGORIES.ADDED
      .catch ApiError, (err) ->
        response = err.message
        if /email restricted/.test(response)
          CATEGORIES.RESTRICTED
        else if /already invited|already in organization/.test(response)
          CATEGORIES.EXISTING
        else if /Must reactivate/.test(response)
          CATEGORIES.MUST_REACTIVATE
        else if /username not found/.test(response)
          CATEGORIES.USERNAME_NOT_FOUND
        else if /Must first transfer account to the/.test(response)
          CATEGORIES.NOT_IN_ENTERPRISE
        else if /No Enterprise licenses/.test(response)
          CATEGORIES.NO_ENTERPRISE_LICENSES
        else if /rate limit|invitation quota|sign-up quota/.test(response)
          CATEGORIES.RATE_LIMIT
        else if /ORGANIZATION_TOO_MANY_MEMBERSHIPS/.test(response)
          CATEGORIES.TOO_MANY_MEMBERS
        else if /MEMBER_TOO_MANY_MEMBERSHIPS/.test(response)
          CATEGORIES.MEMBER_TOO_MANY_ORGS
        else if /DEACTIVATED_IN_ENTERPRISE/.test(response)
          CATEGORIES.DEACTIVATED_IN_THE_ENTERPRISE
        else if /must be a managed enterprise member or have valid email/.test(response)
          CATEGORIES.MUST_BE_MANAGED_ENT_MEMBER_OR_VALID_EMAIL
        else if /must be a managed enterprise member/.test(response)
          CATEGORIES.MUST_BE_MANAGED_ENT_MEMBER
        else
          CATEGORIES.UNKNOWN
      .then (state) ->
        completed++
        updateProgress(completed)
        username = user.attributes?.username || user.username

        {
          user: username || user.email || user,
          state
        }
    , { concurrency: 5 })
    .then (results) =>
      groupUsersByCategory = (results) ->
        _.chain(results)
        .groupBy('state')
        .pairs()
        .map ([category, entries]) -> [category, sortByUser(entries)]
        .object()
        .value()

      sortByUser = (entries) ->
        _.chain(entries)
        .pluck('user')
        .sortBy (user) -> user.toLowerCase().replace(/@.*$/, '')
        .value()

      grouped = groupUsersByCategory(results)
      grouped._categories = CATEGORIES
      grouped._categoryOrder = CATEGORY_ORDER

      if !options?.ignoreErrors
        @updateBulkAddMemberErrors(_.omit(grouped, CATEGORIES.ADDED))

      grouped

  getGoogleAppsMappings: ->
    ApiPromise
      url: "/1/organizations/#{ @id }/googleApps/mappings"

  ensureSlackAssociation: ->
    @_updateSlackAssociationOnce ?= @updateSlackAssociation()

  updateSlackAssociation: ->
    SlackAjax.ajaxTrello
      url: "/trello/#{ @id }/association"
    .tap (response) =>
      [association, status] = response
      if status == "success"
        if association?.associated && !association.teams
          # upgrade old response schema to new schema
          @_slackAssociation =
            associated: true,
            canChange: association.canChange,
            adminOnly: association.adminOnly,
            teams: [{
              domain: association.domain,
              name: association.domain,
              icon: window.slackTrelloDomain + '/img/slack-default-logo.png',
              id: association.id,
              selfJoin: association.selfJoin
            }]
        else
          @_slackAssociation = association
        @trigger 'change:slack-association'

  deleteSlackAssociation: (idSlackTeam) ->
    SlackAjax.ajaxTrello
      url: "/trello/#{ @id }/association?idSlackTeam=#{ idSlackTeam }"
      type: 'DELETE'
    .tap =>
      @updateSlackAssociation()

  getLatestSlackAssociation: -> @_slackAssociation

  changeSlackSelfJoin: (enabled, idSlackTeam) ->
    SlackAjax.ajaxTrello
      url: "/trello/#{ @id }/selfJoin?idSlackTeam=#{ idSlackTeam }&value=#{ enabled }"
      type: 'PUT'
    .tap =>
      @updateSlackAssociation()

  changeSlackAdminOnlyLinking: (adminOnly) ->
    SlackAjax.ajaxTrello
      url: "/trello/#{ @id }/adminOnlyLinking?value=#{ adminOnly }"
      type: 'PUT'
    .tap =>
      @updateSlackAssociation()

  getEnterprise: ->
    @modelCache.get('Enterprise', @get('idEnterprise'))

  fetchCollaborators: ->
    ApiPromise
      url: "/1/organizations/#{ @id }/collaborators"
    .then (data) =>
      @updateCollaboratorList(data)

  updateCollaboratorList: (collaboratorArr) ->
    @set 'collaborators', collaboratorArr
    @trigger 'change:collaborators'

  requestToJoinAnEnterprise: (enterprise) ->
    ApiPromise
      url: "/1/organizations/#{ @id }/enterpriseJoinRequest"
      type: 'PUT'
      data:
        value: enterprise.get('id')
    .then ({ enterpriseJoinRequest }) =>
      unless _.isEmpty(enterpriseJoinRequest)
        enterprise: @modelCache.get('Enterprise', enterpriseJoinRequest.idEnterprise)
        member: @modelCache.get('Member', enterpriseJoinRequest.idMember)

  getEnterpriseJoinRequest: ->
    ApiPromise
      url: "/1/organizations/#{ @id }/enterpriseJoinRequest"
    .then (joinRequest) =>
      unless _.isEmpty(joinRequest)
        enterprise: @modelCache.get('Enterprise', joinRequest.idEnterprise)
        member: @modelCache.get('Member', joinRequest.idMember)

  cancelEnterpriseJoinRequest: ->
    ApiPromise
      url: "/1/organizations/#{ @id }/enterpriseJoinRequest"
      type: 'DELETE'

  getRestrictedAttachmentTypes: ->
    if @getPref('attachmentRestrictions')?
      _.difference(AttachmentTypes, @getPref('attachmentRestrictions'))
    else
      []

  attachmentTypeRestricted: (attachmentType) ->
    attachmentType in @getRestrictedAttachmentTypes()

  attachmentUrlRestricted: (url) ->
    attachmentType = switch
      when isTrello(url) then 'trello'
      when /// docs\.google\.com/ ///i.test(url) ||
        /// drive\.google\.com/ ///i.test(url) then 'google-drive'
      when /// dropbox\.com/ ///i.test(url) then 'dropbox'
      when /// onedrive\.live\.com/ ///i.test(url) ||
        /// 1drv\.ms/ ///i.test(url) then 'onedrive'
      when /// app\.box\.com/ ///i.test(url) then 'box'
      else 'link'

    @attachmentTypeRestricted(attachmentType)

  getAvailableLicenseCount: ->
    if @get('availableLicenseCount') == null || @get('availableLicenseCount') == undefined
      Infinity
    else
      @get('availableLicenseCount')

  loadLicenses: ->
    # Dependency required at call site to avoid import cycles, do not lift to top of module
    ModelLoader = require 'db/model-loader'
    ModelLoader.loadOrganizationMaximumAndAvailableLicenseCount @id

  getFreeBoardLimit: ->
    @get('limits')?.orgs?.freeBoardsPerOrg

  # The number of free boards the org can still create, or null if the limit is not close
  # or relevant (eg. this is a premium team)
  getFreeBoardsRemaining: ->
    limit = @getFreeBoardLimit()
    delta = null

    if !@isPremium() && limit?.disableAt && limit?.count
      delta = limit.disableAt - limit.count
      if delta < 0
        return 0
      else
        return delta

    return null

  # The number of free boards an org has exceeded the limit by. This will only apply
  # to orgs grandfathered through the board limits, with more than 10 boards.
  getFreeBoardsOver: ->
    limit = @getFreeBoardLimit()
    delta = null

    if limit?.disableAt && limit?.count
      delta = limit.disableAt - limit.count

    if delta? && delta < 0 then Math.abs(delta) else 0

  # Whether the org is within 3 boards of their free board limit, or it has been
  # met / exceeded.
  isCloseToFreeBoardLimit: ->
    remaining = @getFreeBoardsRemaining()
    remaining? && remaining <= 3

  # Whether the org has reached their limit, or it is exceeded.
  isAtOrOverFreeBoardLimit: ->
    remaining = @getFreeBoardsRemaining()
    remaining? && remaining == 0

  isFreeBoardLimitOverridden: ->
    limit = @getFreeBoardLimit()
    limit?.disableAt && limit?.disableAt != 10

  incrementFreeBoardLimitCount: (incrementBy) ->
    limits = @get('limits')
    openBoardCount = limits?.orgs?.freeBoardsPerOrg?.count?

    if !openBoardCount
      return

    { count, disableAt, warnAt, status } = limits.orgs.freeBoardsPerOrg

    # calculate the new count
    count += incrementBy

    # calculate the new status
    if count > disableAt
      status = 'maxExceeded'
    else if count == disableAt
      status = 'disabled'
    else if count >= warnAt
      status = 'warn'
    else
      status = 'ok'

    # optimistically update the limit
    limits.orgs.freeBoardsPerOrg.count = count
    limits.orgs.freeBoardsPerOrg.status = status
    @set({ limits })

  getUrl: ->
    Controller.getOrganizationUrl(@get('name'))

  getBillingUrl: ->
    @getUrl() + '/billing'

  getPrivateBoardCount: ->
    openBoards = @boardList.models.filter(
      (board) -> !board.attributes.closed
    )
    @getFreeBoardLimit()?.count - openBoards.length

  loadPlugins: (opts = {}) ->
    # Dependency required at call site to avoid import cycles, do not lift to top of module
    ModelLoader = require 'db/model-loader'

    if @plugins and !opts.force
      return Promise.resolve()

    @pluginsLoading = true
    ModelLoader.loadOrganizationPlugins(@id)
      .then (plugins) =>
        @plugins = plugins
        @pluginsLoading = false
      .catch (err) =>
        @pluginsLoading = false
        throw err



// WEBPACK FOOTER //
// ./app/scripts/models/organization.coffee